import React, { useState, useEffect } from "react";
import { View, StyleSheet, TouchableOpacity } from "react-native";
import { useGetThreadsByCourseQuery } from "../../../graphql/generated/graphql";
import ThreadCardList from "../../../components/customCoursework/StudentDiscussions/ThreadCardList/ThreadCardList";
import StudentDiscussionContainer from "./StudentDiscussionContainer";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import CustomSpacing from "../../../components/common/layout/CustomSpacing/CustomSpacing";

interface Author {
  id: string;
  name: string;
}

interface Commenter {
  id: string;
  name: string;
}

interface LatestComment {
  author: Commenter;
  timestamp: string;
}

interface Post {
  id: string;
  created: string;
  depth: number;
}

interface Thread {
  id: string;
  title: string;
  message: string;
  author: Author;
  responseCount: number;
  commenters: Commenter[];
  latestComment: LatestComment;
  posts?: Post[];
}

interface StudentCommunityContainerProps {
  courseId: string;
}

const StudentCommunityContainer: React.FC<StudentCommunityContainerProps> = ({ courseId }) => {
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const [selectedThreadTitle, setSelectedThreadTitle] = useState<string>("");
  const [returnedToList, setReturnedToList] = useState(false);

  const {
    data: threadsData,
    loading,
    error,
    refetch: refetchThreads,
  } = useGetThreadsByCourseQuery({
    variables: {
      courseId,
    },
    skip: !courseId,
    fetchPolicy: returnedToList ? "cache-and-network" : "cache-first",
  });

  useEffect(() => {
    if (returnedToList) {
      refetchThreads();
      setReturnedToList(false);
    }
  }, [returnedToList, refetchThreads]);

  const rawThreads = threadsData?.getThreadsByCourse?.data || [];

  // Transform the raw thread data to match ThreadCardList interface
  const threads: Thread[] = rawThreads.map((thread) => {
    // Find the initial post (depth 0) to get the main message
    const initialPost = thread.posts?.find((post) => post.depth === 0);

    // Get all response posts (depth 1+) to calculate response count
    const responsePosts = thread.posts?.filter((post) => post.depth > 0) || [];

    // Get unique commenters (authors of response posts)
    const uniqueCommenters = new Map();
    responsePosts.forEach((post) => {
      if (post.author && !uniqueCommenters.has(post.author.id)) {
        uniqueCommenters.set(post.author.id, {
          id: post.author.id,
          name: post.author.name,
        });
      }
    });

    // Get the most recent comment
    const sortedPosts = [...responsePosts].sort(
      (a, b) => new Date(b.created).getTime() - new Date(a.created).getTime()
    );
    const latestPost = sortedPosts[0];

    const transformedPosts = (thread.posts || []).map((post) => ({
      id: post.id,
      created: post.created,
      depth: post.depth,
    }));

    return {
      id: thread.id,
      title: thread.title,
      message: initialPost?.message || "",
      author: {
        id: initialPost?.author?.id || "",
        name: initialPost?.author?.name || "Unknown Author",
      },
      responseCount: responsePosts.length,
      commenters: Array.from(uniqueCommenters.values()),
      latestComment: latestPost
        ? {
            author: {
              id: latestPost.author?.id || "",
              name: latestPost.author?.name || "Unknown",
            },
            timestamp: latestPost.created,
          }
        : {
            author: { id: "", name: "" },
            timestamp: thread.created,
          },
      posts: transformedPosts,
    };
  });

  const handleThreadPress = (threadId: string) => {
    const selectedThread = threads.find((thread) => thread.id === threadId);
    setSelectedThreadId(threadId);
    setSelectedThreadTitle(selectedThread?.title || "Discussion");
    setReturnedToList(false);
  };

  const handleBackToThreadList = () => {
    setSelectedThreadId(null);
    setSelectedThreadTitle("");
    setReturnedToList(true);
  };

  // Show individual discussion view
  if (selectedThreadId) {
    return (
      <View style={styles.container}>
        {/* Breadcrumb Navigation */}
        <View style={styles.breadcrumbContainer}>
          <TouchableOpacity
            onPress={handleBackToThreadList}
            activeOpacity={0.7}
            accessibilityLabel="Back to thread list"
            accessibilityRole="button">
            <CustomText
              text="Course discussion board"
              size="s"
              weight="medium"
              style={styles.breadcrumbLink}
              useTranslationText={false}
            />
          </TouchableOpacity>
          <CustomText
            text=" > "
            size="s"
            weight="medium"
            style={styles.breadcrumbSeparator}
            useTranslationText={false}
          />
          <CustomText
            text={selectedThreadTitle}
            size="s"
            role="heading"
            aria-level="2"
            weight="medium"
            style={styles.breadcrumbCurrent}
            useTranslationText={false}
          />
        </View>

        <CustomSpacing size="m" type="vertical" />

        {/* Discussion Container */}
        <StudentDiscussionContainer threadId={selectedThreadId} threadTitle={selectedThreadTitle} />
      </View>
    );
  }

  // Show thread list view
  return (
    <View style={styles.container}>
      {/* Breadcrumb Navigation */}
      <View style={styles.breadcrumbContainer}>
        <CustomText
          text="Course discussion board"
          size="xl"
          weight="medium"
          role="heading"
          aria-level="2"
          style={styles.breadcrumbCurrent}
          useTranslationText={false}
        />
      </View>

      <CustomSpacing size="m" type="vertical" />

      {/* Loading State */}
      {loading && (
        <View style={styles.centerContainer}>
          <CustomText
            text="Loading discussions..."
            size="m"
            weight="medium"
            role="heading"
            aria-level="3"
            style={styles.loadingText}
            useTranslationText={false}
          />
        </View>
      )}

      {/* Error State */}
      {error && !loading && (
        <View style={styles.centerContainer}>
          <CustomText
            text="Error loading discussions"
            size="m"
            weight="medium"
            role="heading"
            aria-level="3"
            style={styles.errorTitle}
            useTranslationText={false}
          />
          <CustomSpacing size="s" type="vertical" />
          <CustomText
            text={error.message || "Please try again later"}
            size="s"
            role="heading"
            aria-level="4"
            style={styles.errorMessage}
            useTranslationText={false}
          />
        </View>
      )}

      {/* Empty State */}
      {!loading && !error && threads.length === 0 && (
        <View style={styles.centerContainer}>
          <CustomText
            text="No discussions yet"
            size="l"
            weight="medium"
            role="heading"
            aria-level="3"
            style={styles.emptyTitle}
            useTranslationText={false}
          />
          <CustomSpacing size="s" type="vertical" />
          <CustomText
            text="Discussions will appear here once your instructor creates them."
            size="m"
            role="heading"
            aria-level="4"
            style={styles.emptyDescription}
            useTranslationText={false}
          />
        </View>
      )}

      {/* Thread List */}
      {!loading && !error && threads.length > 0 && (
        <ThreadCardList threads={threads} onThreadPress={handleThreadPress} />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: 16,
    paddingTop: 16,
  },
  breadcrumbContainer: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 8,
  },
  breadcrumbLink: {
    color: "#3E68FE",
  },
  breadcrumbSeparator: {
    color: "#6B7280",
  },
  breadcrumbCurrent: {
    color: "#101828",
  },
  centerContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingVertical: 80,
    paddingHorizontal: 32,
  },
  loadingText: {
    color: "#6B7280",
  },
  errorTitle: {
    color: "#101828",
    textAlign: "center",
  },
  errorMessage: {
    color: "#6B7280",
    textAlign: "center",
    maxWidth: 400,
  },
  emptyTitle: {
    color: "#101828",
    textAlign: "center",
  },
  emptyDescription: {
    color: "#6B7280",
    textAlign: "center",
    maxWidth: 400,
  },
});

export default StudentCommunityContainer;
